import React from 'react';
import { Lock, Pencil } from 'lucide-react';
import { SellerGroup, PROFILE_OPTIONS } from './types';

type SellerGroupTableProps = {
  data: SellerGroup[];
  editedProfiles: Map<string, string>;
  onProfileChange: (groupId: string, newProfile: string) => void;
};

export const SellerGroupTable: React.FC<SellerGroupTableProps> = ({
  data,
  editedProfiles,
  onProfileChange,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th
              scope="col"
              className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              <div className="flex items-center gap-1.5">
                <Lock className="w-3 h-3 text-gray-400" />
                ID Grupo
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              <div className="flex items-center gap-1.5">
                <Lock className="w-3 h-3 text-gray-400" />
                Nome Grupo
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              <div className="flex items-center gap-1.5">
                <Pencil className="w-3 h-3 text-blue-500" />
                Perfil
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((group) => {
            const isEdited = editedProfiles.has(group.id);
            const currentProfile = isEdited
              ? editedProfiles.get(group.id)!
              : group.profile;

            return (
              <tr
                key={group.id}
                className={`transition-colors ${
                  isEdited
                    ? 'bg-blue-50/50 hover:bg-blue-50'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-xs font-mono font-medium text-gray-700">
                    {group.id}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-900">
                    {group.name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={currentProfile}
                    onChange={(e) => onProfileChange(group.id, e.target.value)}
                    className={`w-full max-w-[200px] pl-3 pr-3 py-2 text-sm rounded-lg border transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
                      isEdited
                        ? 'bg-blue-50 border-blue-300 text-blue-800 font-medium'
                        : currentProfile === ''
                          ? 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    aria-label={`Perfil do grupo ${group.name}`}
                  >
                    {PROFILE_OPTIONS.map((option) => (
                      <option key={option || '__empty__'} value={option}>
                        {option || '-'}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="bg-gray-100 rounded-full p-3 mb-3">
            <Lock className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">Nenhum grupo encontrado</p>
        </div>
      )}
    </div>
  );
};
